import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Question } from '../data/practiceTestQuestions';
import { getPracticeTestQuestions, NY_QUESTION_BANK_SIZE, NY_QUESTIONS_PER_SESSION, NJ_QUESTION_BANK_SIZE, NJ_QUESTIONS_PER_SESSION } from '../data/practiceTestProvider';
import { useAuth } from '../contexts/AuthContext';
import { supabaseHelpers, PracticeTestSession } from '../lib/supabase';

const PracticeTest: React.FC = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<number[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [shuffleKey, setShuffleKey] = useState(0); // Key to trigger re-shuffling
  const [currentSession, setCurrentSession] = useState<PracticeTestSession | null>(null);
  const [hasExistingSession, setHasExistingSession] = useState(false);
  const [answerOptionShuffles, setAnswerOptionShuffles] = useState<Record<number, number[]>>({});
  const hasInitialized = useRef(false);
  const initializedForUserId = useRef<string | null>(null);

  // Shuffle questions using Fisher-Yates algorithm
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // NY only: sample N random indices from a bank (without replacement), then shuffle
  const sampleAndShuffleIndices = (bankSize: number, count: number): number[] => {
    const indices = Array.from({ length: bankSize }, (_, i) => i);
    const sampled: number[] = [];
    for (let i = 0; i < count && indices.length > 0; i++) {
      const j = Math.floor(Math.random() * indices.length);
      sampled.push(indices.splice(j, 1)[0]);
    }
    return shuffleArray(sampled);
  };

  // Shuffle answer options using Fisher-Yates algorithm
  const shuffleAnswerOptions = (options: { text: string; rationale: string; isCorrect: boolean }[]) => {
    const indices = Array.from({ length: options.length }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  };

  // Use state from session if available, otherwise use user's profile state
  // This prevents state switching during an active test session
  const sessionState = currentSession?.state?.toLowerCase();
  const userState = userProfile?.state?.toLowerCase() || 'ga';
  const currentState = sessionState || userState; // Prioritize session state

  // Free accounts must upgrade to access practice tests
  useEffect(() => {
    if (userProfile?.subscription_type === 'free') {
      navigate('/dashboard', { replace: true });
    }
  }, [userProfile?.subscription_type, navigate]);
  
  // Create shuffled questions array based on session or new shuffle
  const questions = useMemo(() => {
    // Use the state from the session if available, otherwise use current state
    const stateForQuestions = currentSession?.state?.toLowerCase() || currentState;
    const stateQuestions = getPracticeTestQuestions(stateForQuestions as any);
    
    if (currentSession && currentSession.shuffled_questions.length > 0) {
      // Use the saved shuffled order from the session
      return currentSession.shuffled_questions.map(index => stateQuestions[index]);
    } else {
      // Create new shuffled order
      return shuffleArray(stateQuestions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession, shuffleKey, currentState]);

  // Load existing session or create new one
  useEffect(() => {
    const initializeSession = async () => {
      if (!user) {
        setIsLoading(false);
        hasInitialized.current = false;
        initializedForUserId.current = null;
        return;
      }

      // Reset if this is a different user
      if (initializedForUserId.current !== user.id) {
        hasInitialized.current = false;
        initializedForUserId.current = user.id;
      }

      // Don't re-initialize if we already initialized for this user
      if (hasInitialized.current) {
        return;
      }

      hasInitialized.current = true;

      try {
        // Check for existing incomplete session
        const existingSession = await supabaseHelpers.getActivePracticeTestSession(user.id);
        
        if (existingSession) {
          // Resume existing session
          setCurrentSession(existingSession);
          setCurrentQuestionIndex(existingSession.current_question_index);
          setUserAnswers(existingSession.user_answers);
          setScore(existingSession.score);
          setHasExistingSession(true);
          
          // Load or generate shuffled answer options
          let shuffledAnswerOptions: Record<string, number[]> = {};
          if (existingSession.shuffled_answer_options) {
            const parsed = typeof existingSession.shuffled_answer_options === 'string' 
              ? JSON.parse(existingSession.shuffled_answer_options)
              : existingSession.shuffled_answer_options;
            shuffledAnswerOptions = parsed;
          } else {
            // Generate shuffles for old sessions that don't have them
            // If session doesn't have a state, infer it from the question set size
            let stateForQuestions = existingSession.state?.toLowerCase();
            if (!stateForQuestions) {
              // Infer state from question indices - try each state's question set
              const maxIndex = Math.max(...existingSession.shuffled_questions);
              const gaQuestions = getPracticeTestQuestions('ga');
              const flQuestions = getPracticeTestQuestions('fl');
              const caQuestions = getPracticeTestQuestions('ca');
              const txQuestions = getPracticeTestQuestions('tx');
              const nyQuestions = getPracticeTestQuestions('ny');
              const njQuestions = getPracticeTestQuestions('nj');
              
              if (maxIndex < gaQuestions.length) stateForQuestions = 'ga';
              else if (maxIndex < flQuestions.length) stateForQuestions = 'fl';
              else if (maxIndex < caQuestions.length) stateForQuestions = 'ca';
              else if (maxIndex < txQuestions.length) stateForQuestions = 'tx';
              else if (maxIndex < nyQuestions.length) stateForQuestions = 'ny';
              else if (maxIndex < njQuestions.length) stateForQuestions = 'nj';
              else stateForQuestions = 'ga'; // fallback
              
              // Save the inferred state to the session
              await supabaseHelpers.updatePracticeTestSession(existingSession.id, {
                state: stateForQuestions.toUpperCase()
              });
              existingSession.state = stateForQuestions.toUpperCase();
            }
            
            const stateQuestions = getPracticeTestQuestions(stateForQuestions as any);
            const questionsInOrder = existingSession.shuffled_questions.map((index: number) => stateQuestions[index]);
            questionsInOrder.forEach((question: Question, questionIndex: number) => {
              shuffledAnswerOptions[questionIndex.toString()] = shuffleAnswerOptions(question.answerOptions);
            });
            // Save the generated shuffles to the database
            await supabaseHelpers.updatePracticeTestSession(existingSession.id, {
              shuffled_answer_options: shuffledAnswerOptions
            });
          }
          
          // Convert string keys to numbers for local state
          const converted: Record<number, number[]> = {};
          Object.keys(shuffledAnswerOptions).forEach((key: string) => {
            converted[parseInt(key)] = shuffledAnswerOptions[key];
          });
          setAnswerOptionShuffles(converted);
        } else {
          // Create new session - use user's state directly
          const userStateForSession = userProfile?.state?.toLowerCase() || 'ga';
          const stateQuestions = getPracticeTestQuestions(userStateForSession as any);
          let shuffledQuestions: Question[];
          let questionIndices: number[];
          if (userStateForSession === 'ny') {
            questionIndices = sampleAndShuffleIndices(NY_QUESTION_BANK_SIZE, NY_QUESTIONS_PER_SESSION);
            shuffledQuestions = questionIndices.map(i => stateQuestions[i]);
          } else if (userStateForSession === 'nj') {
            questionIndices = sampleAndShuffleIndices(NJ_QUESTION_BANK_SIZE, NJ_QUESTIONS_PER_SESSION);
            shuffledQuestions = questionIndices.map(i => stateQuestions[i]);
          } else {
            shuffledQuestions = shuffleArray(stateQuestions);
            questionIndices = shuffledQuestions.map(q => stateQuestions.indexOf(q));
          }
          
          // Shuffle answer options for each question and store the order
          const shuffledAnswerOptions: Record<string, number[]> = {};
          shuffledQuestions.forEach((question: Question, questionIndex: number) => {
            shuffledAnswerOptions[questionIndex.toString()] = shuffleAnswerOptions(question.answerOptions);
          });
          
          // Store the state in the session to prevent state switching during the test
          const stateCode = userProfile?.state?.toUpperCase() || 'GA';
          
          const newSession = await supabaseHelpers.createPracticeTestSession({
            user_id: user.id,
            current_question_index: 0,
            user_answers: [],
            shuffled_questions: questionIndices,
            shuffled_answer_options: shuffledAnswerOptions,
            score: 0,
            is_completed: false,
            state: stateCode
          });
          
          setCurrentSession(newSession);
          // Convert string keys to numbers for local state
          const converted: Record<number, number[]> = {};
          Object.keys(shuffledAnswerOptions).forEach((key: string) => {
            converted[parseInt(key)] = shuffledAnswerOptions[key];
          });
          setAnswerOptionShuffles(converted);
        }
      } catch (error) {
        console.error('Error initializing practice test session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Only run when user changes, not when userProfile changes (intentionally excluded to prevent re-initialization)

  // Save progress to database
  const saveProgress = useCallback(async (questionIndex: number, answers: number[]) => {
    if (!currentSession || !user) return;
    
    try {
      await supabaseHelpers.updatePracticeTestSession(currentSession.id, {
        current_question_index: questionIndex,
        user_answers: answers
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  }, [currentSession, user]);

  const handleAnswerSelect = (answerIndex: number) => {
    if (userAnswers[currentQuestionIndex] !== undefined) return; // Already answered
    
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = answerIndex;
    setUserAnswers(newAnswers);
    
    // Save progress immediately when answer is selected
    saveProgress(currentQuestionIndex, newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setShowHint(false);
      
      // Save progress when moving to next question
      saveProgress(nextIndex, userAnswers);
    } else {
      calculateResults();
      setShowResults(true);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      const prevIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIndex);
      setShowHint(false);
      
      // Save progress when going back
      saveProgress(prevIndex, userAnswers);
    }
  };

  const calculateResults = async () => {
    let correctAnswers = 0;
    userAnswers.forEach((shuffledAnswerIndex: number, questionIndex: number) => {
      if (shuffledAnswerIndex !== undefined) {
        // Map shuffled answer index back to original index
        const shuffleOrder = answerOptionShuffles[questionIndex] || [];
        const originalAnswerIndex = shuffleOrder[shuffledAnswerIndex];
        if (originalAnswerIndex !== undefined && questions[questionIndex].answerOptions[originalAnswerIndex].isCorrect) {
          correctAnswers++;
        }
      }
    });
    setScore(correctAnswers);
    
    // Save final results to database
    if (currentSession) {
      try {
        await supabaseHelpers.completePracticeTestSession(currentSession.id, correctAnswers);
      } catch (error) {
        console.error('Error saving final results:', error);
      }
    }
  };

  const handleRestart = async () => {
    if (!user) return;

    try {
      // Create new session - use user's state directly
      const userStateForSession = userProfile?.state?.toLowerCase() || 'ga';
      const stateQuestions = getPracticeTestQuestions(userStateForSession as any);
      let shuffledQuestions: Question[];
      let questionIndices: number[];
      if (userStateForSession === 'ny') {
        questionIndices = sampleAndShuffleIndices(NY_QUESTION_BANK_SIZE, NY_QUESTIONS_PER_SESSION);
        shuffledQuestions = questionIndices.map(i => stateQuestions[i]);
      } else if (userStateForSession === 'nj') {
        questionIndices = sampleAndShuffleIndices(NJ_QUESTION_BANK_SIZE, NJ_QUESTIONS_PER_SESSION);
        shuffledQuestions = questionIndices.map(i => stateQuestions[i]);
      } else {
        shuffledQuestions = shuffleArray(stateQuestions);
        questionIndices = shuffledQuestions.map(q => stateQuestions.indexOf(q));
      }

      // Shuffle answer options for each question and store the order
      const shuffledAnswerOptions: Record<string, number[]> = {};
      shuffledQuestions.forEach((question: Question, questionIndex: number) => {
        shuffledAnswerOptions[questionIndex.toString()] = shuffleAnswerOptions(question.answerOptions);
      });
      
      // Store the state in the session to prevent state switching during the test
      const stateCode = userProfile?.state?.toUpperCase() || 'GA';
      
      const newSession = await supabaseHelpers.createPracticeTestSession({
        user_id: user.id,
        current_question_index: 0,
        user_answers: [],
        shuffled_questions: questionIndices,
        shuffled_answer_options: shuffledAnswerOptions,
        score: 0,
        is_completed: false,
        state: stateCode
      });
      
      setCurrentSession(newSession);
      setCurrentQuestionIndex(0);
      setUserAnswers([]);
      setShowResults(false);
      setShowHint(false);
      setScore(0);
      setHasExistingSession(false);
      setShuffleKey(prev => prev + 1); // Trigger new shuffle
      // Convert string keys to numbers for local state
      const converted: Record<number, number[]> = {};
      Object.keys(shuffledAnswerOptions).forEach((key: string) => {
        converted[parseInt(key)] = shuffledAnswerOptions[key];
      });
      setAnswerOptionShuffles(converted);
      // Don't reset hasInitialized - we've manually created a new session
    } catch (error) {
      console.error('Error creating new session:', error);
    }
  };

  const getProgressPercentage = () => {
    return ((currentQuestionIndex + 1) / questions.length) * 100;
  };

  const currentQuestion = questions[currentQuestionIndex];
  const userAnswer = userAnswers[currentQuestionIndex];
  const hasAnswered = userAnswer !== undefined;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full border border-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
            <h3 className="text-xl font-bold mb-2 text-gray-800">Loading Practice Test</h3>
            <p className="text-gray-600">Preparing your questions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Resume session banner
  if (hasExistingSession && !showResults) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100 mb-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-2 text-gray-800">Resume Practice Test</h2>
              <p className="text-gray-600 mb-6">
                You have an incomplete practice test. You were on question {currentQuestionIndex + 1} of {questions.length}.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setHasExistingSession(false)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:shadow-lg transition-all transform hover:scale-105"
                >
                  Resume Test
                </button>
                <button
                  onClick={handleRestart}
                  className="bg-gray-200 text-gray-800 px-8 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Start New Test
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const percentage = Math.round((score / questions.length) * 100);
    const passed = percentage >= 75;

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
            <div className="text-center">
              {/* Score Display */}
              <div className="mb-8">
                <div className={`w-32 h-32 mx-auto mb-6 rounded-full flex items-center justify-center ${
                  passed 
                    ? 'bg-gradient-to-br from-green-500 to-emerald-600' 
                    : 'bg-gradient-to-br from-red-500 to-pink-600'
                } shadow-2xl`}>
                  <span className="text-white text-4xl font-bold">{percentage}%</span>
                </div>
                <h2 className="text-3xl font-bold mb-2 text-gray-800">
                  {passed ? 'Congratulations!' : 'Keep Studying!'}
                </h2>
                <p className="text-lg mb-4 text-gray-600">
                  You answered {score} out of {questions.length} questions correctly
                </p>
                <div className={`inline-block px-6 py-3 rounded-2xl text-lg font-semibold ${
                  passed 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {passed ? '🎉 You Passed!' : '📚 More Practice Needed'}
                </div>
              </div>

              {/* Detailed Results */}
              <div className="bg-gray-50 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold mb-4 text-gray-800">Performance Breakdown</h3>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className={`text-3xl font-bold ${passed ? 'text-green-600' : 'text-red-600'}`}>{score}</div>
                    <div className="text-sm text-gray-600">Correct Answers</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-gray-600">{questions.length - score}</div>
                    <div className="text-sm text-gray-600">Incorrect Answers</div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRestart}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                >
                  🔄 Take Test Again
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-gray-100 text-gray-700 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-200 transition-all duration-300"
                >
                  🏠 Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-4xl mx-auto pt-8">
        {/* Header */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Practice Test</h2>
              <p className="text-sm text-gray-600">
                {currentState === 'fl' ? 'Florida' : currentState === 'ca' ? 'California' : currentState === 'tx' ? 'Texas' : currentState === 'ny' ? 'New York' : currentState === 'nj' ? 'New Jersey' : 'Georgia'} Real Estate Exam Prep - {questions.length} Questions
              </p>
              <p className="text-xs text-blue-600 mt-1">🔄 Questions and answer options shuffled for each attempt</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-bold text-blue-600">{Math.round(getProgressPercentage())}%</span>
            <span className="text-sm text-gray-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${getProgressPercentage()}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
          {/* Question */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {currentQuestion.question}
            </h3>
            
            {/* Answer Options */}
            <div className="space-y-4">
              {(() => {
                // Get shuffled order for this question, or use original order if not shuffled yet
                const shuffleOrder = answerOptionShuffles[currentQuestionIndex];
                const optionsToDisplay = shuffleOrder 
                  ? shuffleOrder.map(originalIndex => currentQuestion.answerOptions[originalIndex])
                  : currentQuestion.answerOptions;
                
                return optionsToDisplay.map((option: { text: string; rationale: string; isCorrect: boolean }, displayIndex: number) => {
                  const isSelected = userAnswer === displayIndex;
                  const isCorrect = option.isCorrect;
                  const showFeedback = hasAnswered;
                
                let optionClasses = "w-full p-4 rounded-2xl border-2 transition-all duration-300 text-left";
                let feedbackText = "";
                
                if (showFeedback) {
                  if (isCorrect) {
                    optionClasses += " bg-green-50 border-green-300 text-green-800";
                    feedbackText = "✓ Correct!";
                  } else if (isSelected) {
                    optionClasses += " bg-red-50 border-red-300 text-red-800";
                    feedbackText = "✗ Incorrect";
                  } else {
                    optionClasses += " bg-gray-50 border-gray-200 text-gray-600";
                  }
                } else {
                  optionClasses += " bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50 cursor-pointer";
                }

                  return (
                    <div key={displayIndex} className="relative">
                      <button
                        onClick={() => handleAnswerSelect(displayIndex)}
                        disabled={hasAnswered}
                        className={optionClasses}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{option.text}</span>
                          {showFeedback && feedbackText && (
                            <span className={`font-bold text-sm ${
                              isCorrect ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {feedbackText}
                            </span>
                          )}
                        </div>
                      </button>
                      
                      {/* Rationale */}
                      {showFeedback && (isCorrect || isSelected) && (
                        <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                          <div className="flex items-start space-x-3">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              isCorrect ? 'bg-green-500' : 'bg-red-500'
                            }`}></div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 mb-1">
                                {isCorrect ? 'Why this is correct:' : 'Why this is incorrect:'}
                              </p>
                              <p className="text-sm text-gray-600">{option.rationale}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Hint Section */}
          <div className="mb-8">
            <button
              onClick={() => setShowHint(!showHint)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">{showHint ? 'Hide Hint' : 'Show Hint'}</span>
            </button>
            
            {showHint && (
              <div className="mt-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm text-blue-800">{currentQuestion.hint}</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={handleBack}
              disabled={currentQuestionIndex === 0}
              className="px-6 py-3 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-gray-200"
            >
              ← Back
            </button>
            
            <button
              onClick={handleNext}
              disabled={!hasAnswered}
              className="px-8 py-3 rounded-2xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500"
            >
              {currentQuestionIndex === questions.length - 1 ? 'Finish' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeTest;
